import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Pagination, PaginationProps, Empty, Tag } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import DataProvider from "../../apis";
import { useAppStore } from "../../store";
import { formatCurrency } from "../../utils";
import Loader from "../elements/loader";

interface HistoryItem {
  id: number;
  amountEarned: number;
  dateEarned: string;
  status?: "confirmed" | "cancelled";
}

const statusConfig: Record<string, { color: string; label: string }> = {
  confirmed: { color: "success", label: "Confirmed" },
  cancelled: { color: "#78716c", label: "Cancelled" },
};

export default function Earned() {
  const { doctor } = useAppStore();
  const [data, setData] = useState<HistoryItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(9);

  const fetchData = useCallback(async () => {
    if (!doctor) return;
    setLoading(true);
    try {
      const results = await DataProvider.getHistory(
        doctor.id,
        currentPage,
        pageSize
      );
      setData(results.data.history ?? []);
      setTotalPages(results.data.totalPages ?? 1);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, doctor, pageSize]);

  useEffect(() => {
    fetchData();
  }, [currentPage, fetchData]);

  const onChange: PaginationProps["onChange"] = (page) => {
    setCurrentPage(page);
    setPageSize(9);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-card p-5">
      <h3 className="m-0 text-sm font-semibold text-slate-800 tracking-tight">
        Recently Earned Appointments
      </h3>

      {loading && data === null ? (
        <div className="mt-3">
          <Loader num={3} />
        </div>
      ) : data && data.length > 0 ? (
        <>
          <ul className="m-0 mt-3 list-none space-y-2 p-0">
            {data.map((item) => {
              const status = statusConfig[item.status ?? ""] ?? {
                color: "#94a3b8",
                label: item.status ?? "—",
              };
              return (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3"
                >
                  <div className="min-w-0">
                    <p className="m-0 font-medium text-slate-800 flex items-center gap-1.5">
                      <CalendarOutlined className="text-slate-400" />
                      {moment(item.dateEarned).format("Do MMM, YYYY")}
                    </p>
                    <Tag color={status.color} className="mt-1 text-xs rounded">
                      {status.label}
                    </Tag>
                  </div>
                  <p className="m-0 shrink-0 font-semibold text-green-600">
                    +{formatCurrency(item.amountEarned)}
                  </p>
                </li>
              );
            })}
          </ul>
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <Pagination
                current={currentPage}
                onChange={onChange}
                total={totalPages * pageSize}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className="text-gray-500">Not earned on an appointment yet.</span>
          }
          className="py-8"
        />
      )}
    </div>
  );
}
